/**
 * viewpointBridge.ts
 *
 * Promise wrapper around the window-event bridge to the R3F Canvas
 * (useViewpointBridge.ts), for code that lives outside the Canvas tree:
 * Viewpoints.tsx, ScenesProvider.tsx, Issues.tsx / IssueDetailModal.tsx.
 */

import type { ViewpointCameraState } from '../views/Projects/views/Scene/tabs/Scene/Editor/r3f/utils/viewpointCamera';

const BRIDGE_TIMEOUT_MS = 5000;

function makeRequestId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export interface CapturedViewpoint {
  state: ViewpointCameraState;
  /**
   * base64 JPEG data URL. With the default `fit: 'contain'` it is scaled to fit
   * within the requested box while keeping the live canvas' aspect ratio
   * (600x400 when no size is asked for); with `fit: 'cover'` it is exactly the
   * requested size, centre-cropped out of the canvas.
   */
  snapshot: string;
}

/**
 * How big the captured image should be.
 *
 * With the default `fit: 'contain'` this is an upper bound and the aspect ratio
 * follows the canvas. With `fit: 'cover'` it is the exact output size, and the
 * canvas is centre-cropped to reach it.
 */
export interface SnapshotSize {
  maxWidth: number;
  maxHeight: number;
  fit?: 'contain' | 'cover';
}

/**
 * What a viewpoint thumbnail is captured at.
 *
 * Viewpoint snapshots are only ever shown small — about 136x91 CSS px in the
 * Navigator's viewpoints panel, and a card of roughly 150-350px in the CMS
 * Viewpoints tab — and they are stored inline on the scene document as base64,
 * so every one of them is carried in the scene payload. 300x200 is a quarter of
 * the pixels the old 600x400 cost.
 */
export const VIEWPOINT_SNAPSHOT_SIZE: SnapshotSize = { maxWidth: 300, maxHeight: 200 };

/**
 * What a *scene thumbnail* is captured at: 600x332, the size the server stores
 * (config/images.js sceneThumbnailSize) — exactly 2x the Details tab's 300x166
 * box and about 2x a scene card in the project gallery.
 *
 * `fit: 'cover'` matters as much as the numbers. A contained capture follows
 * the canvas' aspect ratio, so the server has to reshape it to 600x332 and
 * either pads it (transparent bands inside the file, which the card's
 * `background-size: cover` cannot remove) or upscales the short axis. Cropping
 * here instead hands the server exactly what it stores, so the resize is a
 * no-op, nothing is ever enlarged, and the encode is a fifth of a megapixel —
 * cheap enough not to be felt.
 */
export const SCENE_THUMBNAIL_SNAPSHOT_SIZE: SnapshotSize = {
  maxWidth: 600,
  maxHeight: 332,
  fit: 'cover',
};

/** Captures the current R3F camera state + a snapshot of the live canvas. */
export function captureViewpoint(size?: SnapshotSize): Promise<CapturedViewpoint> {
  return new Promise((resolve, reject) => {
    const requestId = makeRequestId();

    const onResult = (e: Event) => {
      const detail = (e as CustomEvent).detail as { requestId: string; state: ViewpointCameraState; snapshot: string };
      if (detail.requestId !== requestId) return;
      clearTimeout(timer);
      window.removeEventListener('r3f:viewpointCaptured', onResult as EventListener);
      resolve({ state: detail.state, snapshot: detail.snapshot });
    };

    const timer = setTimeout(() => {
      window.removeEventListener('r3f:viewpointCaptured', onResult as EventListener);
      reject(new Error('Viewpoint capture timed out — is the R3F scene mounted?'));
    }, BRIDGE_TIMEOUT_MS);

    window.addEventListener('r3f:viewpointCaptured', onResult as EventListener);
    window.dispatchEvent(
      new CustomEvent('r3f:captureViewpoint', {
        detail: {
          requestId,
          maxWidth: size?.maxWidth,
          maxHeight: size?.maxHeight,
          fit: size?.fit,
        },
      }),
    );
  });
}

/** Smoothly (or instantly, animated=false) flies the R3F camera to the given state. */
export function goToCameraState(state: ViewpointCameraState, animated = true): Promise<void> {
  return new Promise((resolve, reject) => {
    const requestId = makeRequestId();

    const onResult = (e: Event) => {
      const detail = (e as CustomEvent).detail as { requestId: string };
      if (detail.requestId !== requestId) return;
      clearTimeout(timer);
      window.removeEventListener('r3f:viewpointArrived', onResult as EventListener);
      resolve();
    };

    const timer = setTimeout(() => {
      window.removeEventListener('r3f:viewpointArrived', onResult as EventListener);
      reject(new Error('Camera navigation timed out — is the R3F scene mounted?'));
    }, BRIDGE_TIMEOUT_MS);

    window.addEventListener('r3f:viewpointArrived', onResult as EventListener);
    window.dispatchEvent(new CustomEvent('r3f:goToViewpoint', { detail: { requestId, state, animated } }));
  });
}
